<template>
  <div
    class="al-grid"
    :class="[
      isFluid ? 'al-container-fluid' : 'al-container',
      customClass,
      { 'has-not-padding': hasNotPadding }
    ]"
  >
    <div class="al-row">
      <template v-for="(n, index) in columns">
        <template v-if="customSizes">
          <div v-if="sizes.length > 0 && sizes[n - 1]" :key="index" :class="sizes[n - 1]">
            <slot :name="'col-' + n" />
          </div>
          <div v-if="sizes.length == 0" :key="index" class="al-col">
            <slot :name="'col-' + n" />
          </div>
        </template>
        <template v-else>
          <div :key="index" class="al-col">
            <slot :name="'col-' + n" />
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
/**
 * grid system uses a series of containers, rows, and columns to layout and align content.
 * It’s built with flexbox and is fully responsive.
 *
 * @displayName al-grid
 */
export default {
  name: 'AlGrid',
  components: {},
  props: {
    customClass: {
      type: String,
      default: ''
    },
    columns: {
      type: Number,
      default: 1
    },
    customSizes: undefined,
    isFluid: {
      type: Boolean,
      default: false
    },
    hasNotPadding: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    sizes() {
      const size = [];
      const items = this.customSizes;

      const prefix = 'al';
      const countSizes = items.length;
      let lastSize = items[0];
      const newCustom = [];

      if (countSizes < this.columns) {
        lastSize = items[countSizes - 1];
      }

      for (let index = 0; index < this.columns; index += 1) {
        if (items[index]) {
          newCustom.push(items[index]);
        } else {
          newCustom.push(lastSize);
        }
      }

      for (let index = 0; index < newCustom.length; index += 1) {
        const sizes = newCustom[index];

        let customSize = '';
        Object.keys(sizes).forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(sizes, key)) {
            const sz = sizes[key];
            if (key === 'default') {
              customSize += `${prefix}-col-${sz} `;
            } else {
              customSize += `${prefix}-col-${key}-${sz} `;
            }
          }
        });

        size[index] = customSize;
      }

      return size;
    }
  },
  mounted() {},
  methods: {}
};
</script>

<style lang="scss">
/**
* Box fill
*/
$box_padding: 15px;

/**
* screen sizes
*/

$width_mobile-S: 320px;
$width_mobile-M: 375px;
$width_mobile-L: 425px;
$width_tablet: 768px;
$width_labtop: 1024px;
$width_labtop-L: 1440px;
$width_4K: 2560px;

/**
* elements containers with media  queries
*/
.al-grid {
  .al-sidebar {
    -ms-flex: 0 1 320px;
    flex: 0 1 320px;
    height: calc(100vh);
    left: 0;
    max-width: 320px;
    overflow-y: auto;
    position: fixed;
    top: 0;
    z-index: 1000;
  }

  .al-sidebar {
    border-right: 1px solid rgba(0, 0, 0, 0.1);
  }

  .al-sidebar ~ .al-content {
    flex: 0 0 calc(100% - 320px);
    left: 320px;
    max-width: calc(100% - 320px);
  }

  .al-container,
  .al-container-fluid {
    margin-left: auto;
    margin-right: auto;
    padding-left: $box_padding;
    padding-right: $box_padding;

    &.has-not-padding {
      padding-left: 0;
      padding-right: 0;
    }
  }

  .al-container {
    max-width: $width_labtop-L;

    &.al-xl {
      max-width: $width_4K;
    }

    @media screen and (max-width: $width_4K) {
      max-width: $width_labtop-L;
    }

    @media screen and (max-width: $width_labtop-L) {
      max-width: $width_labtop;
    }

    @media screen and (max-width: $width_labtop) {
      max-width: $width_tablet;
    }

    @media screen and (max-width: $width_tablet) {
      max-width: $width_mobile-L;
    }

    @media screen and (max-width: $width_mobile-L) {
      max-width: $width_mobile-M;
    }

    @media screen and (max-width: $width_mobile-M) {
      max-width: $width_mobile-S;
    }
  }

  /**
* element row
*/
  .al-row {
    display: -ms-flexbox;
    display: flex;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap;

    margin-left: $box_padding * -1;
    margin-right: $box_padding * -1;
  }

  /**
* element col
*/
  .al-col {
    @extend %al-col;
    flex-basis: 0;
    flex-grow: 1;
    -ms-flex-positive: 1;

    -ms-flex-preferred-size: 0;
    max-width: 100%;
  }

  .al-col,
  [class^='al-col-'],
  [class^='al-col'] {
    @extend %al-col;
  }

  %al-col {
    padding-left: $box_padding;
    padding-right: $box_padding;
    position: relative;
    width: 100%;
  }

  /**
* Grid size for all devices
*/
  @for $i from 1 through 12 {
    $column_size: (100% / 12) * $i;

    .al-col-#{$i} {
      -ms-flex: 0 0 $column_size;
      flex: 0 0 $column_size;
      max-width: $column_size;
    }
  }

  /**
* Grid size for mobile m
*/
  @media screen and (min-width: $width_mobile-S) {
    @for $i from 1 through 12 {
      $column_size: (100% / 12) * $i;

      .al-col-xxs-#{$i} {
        -ms-flex: 0 0 $column_size;
        flex: 0 0 $column_size;
        max-width: $column_size;
      }
    }
  }

  /**
* Grid size for mobile m
*/
  @media screen and (min-width: $width_mobile-M) {
    @for $i from 1 through 12 {
      $column_size: (100% / 12) * $i;

      .al-col-xs-#{$i} {
        -ms-flex: 0 0 $column_size;
        flex: 0 0 $column_size;
        max-width: $column_size;
      }
    }
  }

  /**
* Grid size for mobile l
*/
  @media screen and (min-width: $width_mobile-L) {
    @for $i from 1 through 12 {
      $column_size: (100% / 12) * $i;

      .al-col-sm-#{$i} {
        -ms-flex: 0 0 $column_size;
        flex: 0 0 $column_size;
        max-width: $column_size;
      }
    }
  }

  /**
* Grid size for tablet
*/
  @media screen and (min-width: $width_tablet) {
    @for $i from 1 through 12 {
      $column_size: (100% / 12) * $i;

      .al-col-md-#{$i} {
        -ms-flex: 0 0 $column_size;
        flex: 0 0 $column_size;
        max-width: $column_size;
      }
    }
  }

  /**
* Grid size for labtop
*/
  @media screen and (min-width: $width_labtop) {
    @for $i from 1 through 12 {
      $column_size: (100% / 12) * $i;

      .al-col-lg-#{$i} {
        -ms-flex: 0 0 $column_size;
        flex: 0 0 $column_size;
        max-width: $column_size;
      }
    }
  }

  /**
* Grid size for laptop L
*/
  @media screen and (min-width: $width_labtop-L) {
    @for $i from 1 through 12 {
      $column_size: (100% / 12) * $i;

      .al-col-xl-#{$i} {
        -ms-flex: 0 0 $column_size;
        flex: 0 0 $column_size;
        max-width: $column_size;
      }
    }
  }

  /**
* Grid size for 4K
*/
  @media screen and (min-width: $width_4K) {
    @for $i from 1 through 12 {
      $column_size: (100% / 12) * $i;

      .al-col-xxl-#{$i} {
        -ms-flex: 0 0 $column_size;
        flex: 0 0 $column_size;
        max-width: $column_size;
      }
    }
  }

  .al-h-100 {
    height: 100%;
  }
}
</style>
